import { memo, useState, useEffect, useCallback, useMemo, useRef, type CSSProperties } from 'react'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { programsApi, itemsApi, recurrenceApi, liveSourcesApi, logoProfilesApi, interludeApi, seriesApi, collectionsApi } from '../../lib/api.js'
import { adBlocksApi, infoservicesApi, spotsApi, type InfoserviceResolution } from '../../lib/adsApi.js'
import { AD_BLOCK_BLACKSCREEN_SEC, adBlockBlackDurationSec, adBlockTotalDurationSec } from '../../lib/adBlockTiming.js'
import { useItems, useStore } from '../../store/index.js'
import type { Program, MediaAsset, RecurrenceFreq, LiveSource, LogoProfile, InterludeClip, LiveStartMode, LiveEndMode, AssetPresenceEntry } from '../../types/index.js'
import type { Collection, Series } from '../../types/domain.js'
import { defaultLogoProfileId, formatTime, isBroadcastableMedia, ANTENNA_ELEMENT_PROGRAM_TYPES } from '../../lib/utils.js'
import { ensureWeeklyGrid } from '../../lib/ensureWeeklyGrid.js'
import { beginGridChange, commitGridChange } from '../../lib/gridUndo.js'
import { ModalShell } from '../common/ModalShell.js'
import { MissingFileBadge } from '../common/MissingFileBadge.js'
import {
  broadcastDayKey, broadcastTimeOfDay, instantAtBroadcastClock,
} from '../../lib/broadcast-day.js'
import { getServerNow } from '../../hooks/useServerClock.js'
import { Spinner } from '../common/Spinner.js'
import { formatAdSpotLabel } from '../../lib/adSpotLabel.js'
import {
  ADJACENCY_TOLERANCE_MS, infoserviceClockRange, type InfoserviceInsertPlan, type InfoserviceResizeEdge,
} from './infoserviceResize.js'
import {
  insertionSpaceRequest, occupiedInsertionBoundaryAt, openInsertionSpace, type InsertionRecipe,
} from './insertion.js'
import {
  gapAddAnchor, gapOverflowPlacement, gapOverflowSec, gapSideFeasible, nearestStartAbove, type AddGap,
} from './gapOverflow.js'

/** Heure proposee par defaut : celle du serveur, lue a l'antenne. */
function nowTimeStr(): string {
  return broadcastTimeOfDay(getServerNow())
}

const DAY_LABELS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
const DAY_LABELS_LONG = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']

/**
 * Configuration centralisée des modes de programmation.
 * `AD_BLOCK` n'est pas un mode interne mais ouvre un autre modal — il est rendu
 * dans la même barre pour la cohérence visuelle.
 */
type ItemMode = 'PROGRAM' | 'JINGLE' | 'LIVE' | 'AD_BLOCK' | 'INFOSERVICE'

const MODE_DEFS = [
  { id: 'PROGRAM', label: 'Programme',  title: 'Émission de catalogue avec fichier vidéo',
  },
  { id: 'JINGLE',  label: 'Élément d’antenne', title: 'Élément d’antenne : jingle, coming next, bande-annonce…',
  },
  { id: 'LIVE',    label: 'Direct',     title: 'Direct depuis une source live, avec programme de rediffusion optionnel',
  },
  { id: 'AD_BLOCK', label: 'Bloc pub',  title: 'Composer un bloc publicitaire avec plusieurs spots',
  },
  { id: 'INFOSERVICE', label: 'Infoservice', title: 'Vidéo infoservice choisie par l’utilisateur',
  },
] as const satisfies ReadonlyArray<{ id: ItemMode; label: string; title: string }>

/** Ce que le bouton « Annuler » de la barre d'outils dira de cet ajout. */
const ADD_UNDO_LABELS: Record<ItemMode, string> = {
  PROGRAM:     'ajout d’un programme',
  JINGLE:      'ajout d’un élément d’antenne',
  LIVE:        'ajout d’un direct',
  AD_BLOCK:    'ajout d’un bloc pub',
  INFOSERVICE: 'ajout d’une plage d’infoservice',
}

/**
 * Le sélecteur de programme est en deux temps : on désigne d'abord le lot où
 * piocher, puis le programme dedans. Un sélecteur plat mélangeait tous les
 * épisodes de toutes les séries avec les unitaires, et la limite de 100 lignes
 * de l'API le tronquait sans prévenir.
 */
type ProgramScope = 'SERIES' | 'COLLECTION' | 'UNGROUPED'

const SCOPE_DEFS = [
  { id: 'SERIES',     label: 'Série' },
  { id: 'COLLECTION', label: 'Collection' },
  { id: 'UNGROUPED',  label: 'Hors série' },
] as const satisfies ReadonlyArray<{ id: ProgramScope; label: string }>

/** « S02E05 · » devant le titre, quand la série numérote ses épisodes. */
function episodePrefix(program: Program): string {
  if (program.season == null && program.episode == null) return ''
  const season  = program.season  != null ? `S${String(program.season).padStart(2, '0')}`  : ''
  const episode = program.episode != null ? `E${String(program.episode).padStart(2, '0')}` : ''
  return `${season}${episode} · `
}

interface AddItemModalProps {
  channelId:          string
  gridId:             string
  defaultDate:        string
  defaultTime?:       string
  anchorMode?:        'START' | 'END'
  /**
   * Manœuvres possibles pour loger l'ajout à une intersection déjà occupée.
   * Deux entrées signifient que l'opérateur peut prendre le temps sur
   * l'infoservice du haut ou sur celui du bas.
   */
  insertPlans?:       InfoserviceInsertPlan[]
  /**
   * Trou libre à combler, avec les infoservices qui le bordent. Quand l'élément
   * posé dépasse le trou, le surplus se prend sur l'infoservice du haut ou du
   * bas, au choix. `null` = ajout ordinaire sans débordement à gérer.
   */
  insertGap?:         AddGap | null
  canInsert?:         boolean
  onCreated:          () => void
  /**
   * Insertion réussie par le plan ci-dessus. Le parent s'en sert pour proposer
   * la même manœuvre sur les copies de la séquence ailleurs dans la semaine.
   */
  onInserted?:        (info: { plan: InfoserviceInsertPlan; recipe: InsertionRecipe; gridId: string }) => void
  onClose:            () => void
  defaultProgramId?:  string
  defaultItemType?:   'PROGRAM' | 'JINGLE'
}

// ─── Types pour le mode AD_BLOCK (sélection multi-spots) ─────────────────────

interface AdBlockSpotEntry {
  spotId:         string
  title:          string
  durationSec:    number
  campaignName:   string | null
  advertiserName: string | null
  /** Identifiant interne stable pour le rendu (un même spot peut apparaître plusieurs fois). */
  rowKey:         string
}

interface AdBlockCatalogSpot {
  id:             string
  title:          string
  durationSec:    number
  active:         boolean
  campaign?:      { id: string; name: string } | null
  advertiser?:    { id: string; name: string } | null
  /** Présence du fichier sur disque. `null` = non suivi (chemin vide ou flux). */
  presence?:      AssetPresenceEntry | null
}

let __adBlockRowKeyCounter = 0
const newAdBlockRowKey = () => `row-${++__adBlockRowKeyCounter}-${Date.now()}`

export const AddItemModal = memo(function AddItemModal({
  channelId, gridId, defaultDate, defaultTime, anchorMode = 'START',
  insertPlans = [], insertGap = null, canInsert = false, onCreated, onInserted, onClose,
  defaultProgramId, defaultItemType,
}: AddItemModalProps) {
  const storeItems = useItems()
  const [programs,       setPrograms]       = useState<Program[]>([])
  const [resolvedGrid,   setResolvedGrid]   = useState(gridId)
  const [progId,         setProgId]         = useState(defaultProgramId ?? '')
  const [assetId,        setAssetId]        = useState('')
  const [assets,         setAssets]         = useState<MediaAsset[]>([])
  const [date,           setDate]           = useState(defaultDate)
  const [time,           setTime]           = useState(defaultTime ?? nowTimeStr())
  const [infoserviceStartTime, setInfoserviceStartTime] = useState(defaultTime ?? nowTimeStr())
  const [infoserviceEndTime, setInfoserviceEndTime] = useState('')
  const [duration,       setDuration]       = useState(0)
  const [itemType,       setItemType]       = useState<ItemMode>(
    defaultItemType ?? 'PROGRAM'
  )
  const [loading,        setLoading]        = useState(false)
  const [loadingP,       setLoadingP]       = useState(true)
  const [error,          setError]          = useState('')
  // Le bouton global « Ajouter » ne connaît pas encore la géométrie du point
  // choisi. Dès que l'opérateur saisit l'heure exacte d'une intersection, on
  // retrouve ici le même plan que le bouton « Insérer » de la grille.
  const enteredBoundary = useMemo(() => {
    if (insertGap || insertPlans.length > 0 || !date || !time) return { occupied: false, plans: [] }
    const boundaryMs = instantAtBroadcastClock(date, time).getTime()
    if (!Number.isFinite(boundaryMs)) return { occupied: false, plans: [] }
    // Un trou volontaire de quelques secondes reste dans la tolérance de
    // proximité : sans ce test, l'heure saisie au creux d'une réserve laissée
    // pour un jingle passerait pour une intersection et décalerait la suite
    // alors que la place existe déjà. On ne recale que si le contenu déborde.
    const live = storeItems.filter(item => item.status !== 'SKIPPED')
    const nextStart = nearestStartAbove(live, '', boundaryMs)
    if (duration > 0 && nextStart !== null && nextStart - boundaryMs >= duration * 1_000) {
      return { occupied: false, plans: [] }
    }
    return occupiedInsertionBoundaryAt(storeItems, boundaryMs, ADJACENCY_TOLERANCE_MS)
  }, [date, duration, insertGap, insertPlans.length, storeItems, time])
  const effectiveInsertPlans = canInsert
    ? insertPlans.length > 0 ? insertPlans : enteredBoundary.plans
    : []

  // À une intersection déjà occupée, le choix haut/bas porte sur les plans
  // explicites ou détectés ; sur un trou trop petit, sur les deux plages qui le
  // bordent (`insertGap`).
  const choosablePlans = useMemo(
    () => insertGap ? insertGap.plans : effectiveInsertPlans,
    [effectiveInsertPlans, insertGap],
  )
  const [insertEdge,     setInsertEdge]     = useState<InfoserviceResizeEdge>(
    // Côté par défaut = celui vers lequel l'ajout grandit : le haut pour un
    // ajout collé au bas d'un bloc (fin ancrée), le bas dans l'autre sens.
    choosablePlans.find(plan => plan.edge === anchorMode)?.edge ?? choosablePlans[0]?.edge ?? anchorMode,
  )

  const selectedInsertPlan = useMemo(
    () => choosablePlans.find(plan => plan.edge === insertEdge) ?? choosablePlans[0] ?? null,
    [insertEdge, choosablePlans],
  )

  // ── Mode « trou » : l'ajout comble une brèche, et déborde peut-être ─────────
  // Le débordement (durée au-delà du trou) se prend sur l'infoservice du côté
  // choisi ; sans débordement, l'ajout reste ordinaire et ne touche personne.
  const overflowSec = insertGap ? gapOverflowSec(insertGap, duration) : 0
  const gapMode = !!insertGap && overflowSec > 0
  const gapPlan = gapMode ? selectedInsertPlan : null
  const gapSideOk = gapSideFeasible(gapPlan, overflowSec)
  // Dans un trou, le côté d'infoservice ne décide de l'ancrage qu'en cas de
  // débordement ; sinon c'est le bord cliqué (`gapAddAnchor`).
  const gapAnchor = insertGap ? gapAddAnchor(insertGap, duration, anchorMode, selectedInsertPlan?.edge) : null
  const effectiveAnchorMode = gapAnchor?.anchor ?? selectedInsertPlan?.edge ?? anchorMode
  const gapAnchorEdgeMs = gapAnchor?.edgeMs ?? null

  // ── Mode AD_HOC : direct créé sans entrée au catalogue ─────────────────────
  const [adHocTitle,     setAdHocTitle]     = useState('')
  const [adHocSourceId,  setAdHocSourceId]  = useState('')
  const [liveSources,    setLiveSources]    = useState<LiveSource[]>([])
  const [logoProfiles,   setLogoProfiles]   = useState<LogoProfile[]>([])
  const [createReplayProgram, setCreateReplayProgram] = useState(false)
  const [livePartDurationsMin, setLivePartDurationsMin] = useState(['60'])
  const [liveInterPartBreakMin, setLiveInterPartBreakMin] = useState('0')
  const [liveTitleShort, setLiveTitleShort] = useState('')
  const [liveSubtitle,   setLiveSubtitle]   = useState('')
  const [liveSummary,    setLiveSummary]    = useState('')
  const [liveGenre,      setLiveGenre]      = useState('')
  const [liveTags,       setLiveTags]       = useState('')
  const [liveDirector,   setLiveDirector]   = useState('')
  const [livePresenter,  setLivePresenter]  = useState('')
  const [liveProduction, setLiveProduction] = useState('')
  const [liveYear,       setLiveYear]       = useState('')
  const [liveIsNew,      setLiveIsNew]      = useState(true)
  const [liveFederation, setLiveFederation] = useState('')
  const [liveThemeId,    setLiveThemeId]    = useState('')
  const [liveShowId,     setLiveShowId]     = useState('')
  const [liveLogoProfileId, setLiveLogoProfileId] = useState('')
  const [liveStartMode, setLiveStartMode] = useState<LiveStartMode>('AUTO')
  const [liveEndMode, setLiveEndMode] = useState<LiveEndMode>('AUTO')
  const [liveHoldClipId, setLiveHoldClipId] = useState('')
  const [interludeClips, setInterludeClips] = useState<InterludeClip[]>([])

  // ── Toggle "inclure les directs ad-hoc" pour les modes LIVE et PROGRAM ────
  const [includeAdHoc,   setIncludeAdHoc]   = useState(false)

  // ── Mode PROGRAM : sélection en deux temps (lot puis programme) ───────────
  const [scope,          setScope]          = useState<ProgramScope>('SERIES')
  const [scopeSeriesId,  setScopeSeriesId]  = useState('')
  const [scopeCollId,    setScopeCollId]    = useState('')
  const [seriesList,     setSeriesList]     = useState<Series[]>([])
  const [collections,    setCollections]    = useState<Collection[]>([])
  const [loadingScope,   setLoadingScope]   = useState(false)
  const [progSearch,     setProgSearch]     = useState('')
  const [progQuery,      setProgQuery]      = useState('')
  /** `true` quand la liste renvoyée est tronquée par la limite de l'API. */
  const [progTruncated,  setProgTruncated]  = useState(false)
  /**
   * Un programme pré-sélectionné (bouton « Programmer » du catalogue) peut
   * appartenir à n'importe quel lot : tant qu'on n'a pas lu sa fiche, on ne
   * sait pas quel lot ouvrir, et charger la liste du lot par défaut ferait
   * perdre la sélection.
   */
  const [scopeResolved,  setScopeResolved]  = useState(!defaultProgramId)

  // ── Mode AD_BLOCK : sélection multi-spots pour composer un bloc pub ───────
  const [adBlockEntries, setAdBlockEntries] = useState<AdBlockSpotEntry[]>([])
  const [adBlockCatalog, setAdBlockCatalog] = useState<AdBlockCatalogSpot[]>([])
  const [adBlockSearch,  setAdBlockSearch]  = useState('')
  const [adBlockShowAdd, setAdBlockShowAdd] = useState(false)
  const [infoserviceResolution, setInfoserviceResolution] = useState<InfoserviceResolution | null>(null)
  const adBlockSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  // ── État récurrence ──────────────────────────────────────────────────────
  const [recEnabled,    setRecEnabled]    = useState(false)
  const [recFreq,       setRecFreq]       = useState<RecurrenceFreq>('WEEKLY')
  const [recInterval,   setRecInterval]   = useState(1)
  const [recDaysOfWeek, setRecDaysOfWeek] = useState<number[]>([])
  const [recDaysOfMonth,setRecDaysOfMonth]= useState<number[]>([])
  const [recEndMode,    setRecEndMode]    = useState<'never' | 'date' | 'count'>('never')
  const [recEndDate,    setRecEndDate]    = useState('')
  const [recCount,      setRecCount]      = useState(4)

  const pendingSelectRef = useRef(defaultProgramId ?? '')
  const infoserviceTimesReadyRef = useRef(false)
  const infoserviceAnchorRef = useRef({ date, time })
  infoserviceAnchorRef.current = { date, time }

  const lastItem = useMemo(() => {
    if (!storeItems.length) return null
    return storeItems.reduce((latest, item) =>
      !latest || new Date(item.endTime) > new Date(latest.endTime) ? item : latest
    , null as typeof storeItems[0] | null)
  }, [storeItems])

  const snapToAfterLast = useCallback(() => {
    if (!lastItem) return
    const end = new Date(lastItem.endTime)
    setDate(`${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`)
    setTime(`${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}:${String(end.getSeconds()).padStart(2, '0')}`)
  }, [lastItem])

  // Initialise les jours de semaine avec le jour de la date sélectionnée
  useEffect(() => {
    if (recDaysOfWeek.length === 0 && date) {
      const dow = new Date(date + 'T00:00:00').getDay()
      setRecDaysOfWeek([dow])
    }
  }, [date, recDaysOfWeek.length])

  // Initialise le jour du mois avec le jour de la date sélectionnée
  useEffect(() => {
    if (recDaysOfMonth.length === 0 && date) {
      const dom = new Date(date + 'T00:00:00').getDate()
      setRecDaysOfMonth([dom])
    }
  }, [date, recDaysOfMonth.length])

  // Résolution grille
  useEffect(() => {
    setResolvedGrid(gridId)
  }, [gridId])

  const ensureTargetGrid = useCallback(async (): Promise<string> => {
    if (resolvedGrid) return resolvedGrid

    const grid = await ensureWeeklyGrid(channelId, date)
    setResolvedGrid(grid.id)
    return grid.id
  }, [channelId, date, resolvedGrid])

  // Lots disponibles pour le mode PROGRAM
  useEffect(() => {
    if (itemType !== 'PROGRAM') return
    setLoadingScope(true)
    Promise.all([
      seriesApi.list({ channelId }).catch(() => [] as Series[]),
      collectionsApi.list({ channelId }).catch(() => [] as Collection[]),
    ])
      .then(([series, cols]) => {
        setSeriesList(series)
        setCollections(cols)
      })
      .finally(() => setLoadingScope(false))
  }, [channelId, itemType])

  // Lot d'origine d'un programme pré-sélectionné
  useEffect(() => {
    if (scopeResolved || !defaultProgramId) return
    let cancelled = false
    programsApi.getById(defaultProgramId)
      .then(async program => {
        const root = program.parentProgramId
          ? await programsApi.getById(program.parentProgramId)
          : program
        if (cancelled) return
        if (root.seriesId) {
          setScope('SERIES')
          setScopeSeriesId(root.seriesId)
        } else if (root.collections?.length) {
          setScope('COLLECTION')
          setScopeCollId(root.collections[0].id)
        } else {
          setScope('UNGROUPED')
        }
      })
      .catch(() => { if (!cancelled) setScope('UNGROUPED') })
      .finally(() => { if (!cancelled) setScopeResolved(true) })
    return () => { cancelled = true }
  }, [defaultProgramId, scopeResolved])

  // La recherche interroge le serveur : la liste est plafonnée à 100 lignes,
  // filtrer côté navigateur ne verrait donc pas au-delà.
  useEffect(() => {
    const handle = setTimeout(() => setProgQuery(progSearch.trim()), 250)
    return () => clearTimeout(handle)
  }, [progSearch])

  // Chargement selon itemType
  useEffect(() => {
    setLoadingP(true)
    setProgId('')
    setAssets([])

    if (itemType === 'LIVE') {
      Promise.all([
        liveSourcesApi.list(channelId),
        logoProfilesApi.list(channelId),
        interludeApi.list(channelId),
      ])
        .then(([sources, profiles, clips]) => {
          setLiveSources(sources)
          setLogoProfiles(profiles)
          setInterludeClips(clips.filter(clip => clip.active && Boolean(clip.filePath)))
          if (!liveLogoProfileId) {
            setLiveLogoProfileId(defaultLogoProfileId(profiles))
          }
        })
        .catch(() => {
          setLiveSources([])
          setLogoProfiles([])
          setInterludeClips([])
        })
        .finally(() => setLoadingP(false))
    } else if (itemType === 'JINGLE') {
      Promise.all(
        ANTENNA_ELEMENT_PROGRAM_TYPES.map(type =>
          programsApi.list({ channelId, type, limit: 100, sortBy: 'title', sortDir: 'asc' })
            .catch(() => ({ data: [] })),
        ),
      )
        .then(results => {
          const map = new Map<string, Program>()
          results.forEach(r => (r.data ?? []).forEach((p: Program) => map.set(p.id, p)))
          const merged = Array.from(map.values()).sort((a, b) => a.title.localeCompare(b.title, 'fr'))
          setPrograms(merged)
          const psr = pendingSelectRef.current
          if (psr && merged.find((p: Program) => p.id === psr)) setProgId(psr)
        })
        .catch(() => setPrograms([]))
        .finally(() => setLoadingP(false))
    } else if (itemType === 'AD_BLOCK') {
      // Charge le catalogue de spots actifs rattachés à une campagne
      spotsApi.list({ channelId })
        .then((c: any) => {
          setAdBlockCatalog(Array.isArray(c) ? c.filter((s: any) => s.active && s.campaign) : [])
        })
        .catch(() => setAdBlockCatalog([]))
        .finally(() => setLoadingP(false))
    } else if (itemType === 'INFOSERVICE') {
      setInfoserviceResolution(null)
      infoservicesApi.resolve({ channelId })
        .then(resolution => {
          setInfoserviceResolution(resolution)
          if (!infoserviceTimesReadyRef.current) {
            const anchorValue = infoserviceAnchorRef.current
            const anchor = instantAtBroadcastClock(anchorValue.date, anchorValue.time)
            const start = effectiveAnchorMode === 'END'
              ? new Date(anchor.getTime() - resolution.spot.durationSec * 1_000)
              : anchor
            const end = effectiveAnchorMode === 'END'
              ? anchor
              : new Date(anchor.getTime() + resolution.spot.durationSec * 1_000)
            setDate(broadcastDayKey(start))
            setInfoserviceStartTime(broadcastTimeOfDay(start))
            setInfoserviceEndTime(broadcastTimeOfDay(end))
            infoserviceTimesReadyRef.current = true
          }
        })
        .catch(() => setInfoserviceResolution(null))
        .finally(() => setLoadingP(false))
    } else {
      // Tant que le lot n'est pas désigné, il n'y a rien à charger : on ne
      // déverse plus tout le catalogue dans le sélecteur.
      const scopeQuery =
        scope === 'SERIES'     ? (scopeSeriesId ? { seriesId:     scopeSeriesId } : null)
      : scope === 'COLLECTION' ? (scopeCollId   ? { collectionId: scopeCollId   } : null)
      :                          { ungrouped: 'true' as const }

      if (!scopeQuery || !scopeResolved) {
        setPrograms([])
        setProgTruncated(false)
        setLoadingP(false)
        return
      }

      programsApi.list({
        channelId, limit: 100, sortBy: 'title', sortDir: 'asc',
        ...scopeQuery,
        ...(progQuery ? { search: progQuery } : {}),
        // Les directs ad-hoc n'ont ni série ni collection : ils ne peuvent
        // apparaître que dans le lot des unitaires.
        ...(includeAdHoc && scope === 'UNGROUPED' ? { includeAdHoc: 'true' as const } : {}),
      })
        .then(r => {
          // En mode PROGRAM normal, on cache les éléments d'antenne et les programmes live
          // (sauf les ad-hoc déjà diffusés qui sont des candidats valides à la rediff).
          const filtered = (r.data ?? []).filter((p: Program) =>
            !ANTENNA_ELEMENT_PROGRAM_TYPES.includes(p.type) && !p.isLive,
          )
          setPrograms(filtered)
          setProgTruncated((r.meta?.total ?? 0) > (r.data?.length ?? 0))
          const psr = pendingSelectRef.current
          if (psr && filtered.some((p: Program) => p.id === psr || p.parts?.some(part => part.id === psr))) {
            setProgId(psr)
            pendingSelectRef.current = ''
          }
        })
        .catch(() => setPrograms([]))
        .finally(() => setLoadingP(false))
    }
  }, [effectiveAnchorMode, channelId, includeAdHoc, itemType, progQuery, scope, scopeCollId, scopeResolved, scopeSeriesId])

  // Médias du programme sélectionné
  useEffect(() => {
    if (!progId) { setAssets([]); setAssetId(''); setDuration(0); return }
    const root = programs.find(program => program.id === progId || program.parts?.some(part => part.id === progId))
    const prog = root?.id === progId ? root : root?.parts?.find(part => part.id === progId)
    if (root) setDuration(root.blockDurationSec ?? root.durationSec ?? 0)
    // Une réponse arrivée après un changement de programme est ignorée : elle
    // poserait le fichier de l'ancien programme sur la case (10/10/2026).
    let cancelled = false
    programsApi.listMedia(progId)
      .then((list: any) => {
        if (cancelled) return
        const arr = Array.isArray(list) ? list : []
        setAssets(arr)
        // Épisode à deux versions : la longue par défaut, l'opérateur choisit.
        const main = arr.find((a: MediaAsset) => !a.isProxy && a.version === 'LONG')
          ?? arr.find((a: MediaAsset) => !a.isProxy)
        if (main) {
          setAssetId(main.id)
          if (main.durationSec && !(root?.parts?.length)) setDuration(main.durationSec as unknown as number)
        } else {
          // Programme sans fichier : le fichier choisi pour le programme
          // précédent ne reste pas sélectionné, il serait posé sur cette case.
          setAssetId('')
        }
      })
      .catch(() => { if (!cancelled) { setAssets([]); setAssetId('') } })
    return () => { cancelled = true }
  }, [progId, programs])

  // ── Mode AD_BLOCK : durée totale = spots + noirs, synchronisée avec `duration` ──
  const adBlockDurationSec = useMemo(
    () => adBlockTotalDurationSec(adBlockEntries.map(e => e.durationSec)),
    [adBlockEntries],
  )
  const adBlockBlackSec = useMemo(
    () => adBlockBlackDurationSec(adBlockEntries.length),
    [adBlockEntries.length],
  )

  const livePartDurationsSec = useMemo(
    () => livePartDurationsMin.map(value => Math.round(Number(value) * 60)),
    [livePartDurationsMin],
  )
  const liveInterPartBreakSec = Math.round(Number(liveInterPartBreakMin) * 60)
  const liveBlockDurationSec = livePartDurationsSec.reduce((total, value) => total + value, 0)
    + Math.max(0, liveInterPartBreakSec) * Math.max(0, livePartDurationsSec.length - 1)

  useEffect(() => {
    if (itemType === 'LIVE') setDuration(liveBlockDurationSec)
  }, [itemType, liveBlockDurationSec])
  useEffect(() => {
    if (itemType === 'AD_BLOCK') setDuration(adBlockDurationSec)
  }, [itemType, adBlockDurationSec])

  // ── Mode AD_BLOCK : mutations locales ────────────────────────────────────
  const adBlockMoveSpot = useCallback((idx: number, dir: -1 | 1) => {
    setAdBlockEntries(prev => {
      const next = [...prev]
      const target = idx + dir
      if (target < 0 || target >= next.length) return prev
      ;[next[idx], next[target]] = [next[target], next[idx]]
      return next
    })
  }, [])
  const adBlockReorderSpot = useCallback((activeRowKey: string, overRowKey: string) => {
    setAdBlockEntries(prev => {
      const oldIndex = prev.findIndex(e => e.rowKey === activeRowKey)
      const newIndex = prev.findIndex(e => e.rowKey === overRowKey)
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return prev
      return arrayMove(prev, oldIndex, newIndex)
    })
  }, [])
  const adBlockHandleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over) return
    adBlockReorderSpot(String(active.id), String(over.id))
  }, [adBlockReorderSpot])
  const adBlockRemoveSpot = useCallback((rowKey: string) => {
    setAdBlockEntries(prev => prev.filter(e => e.rowKey !== rowKey))
  }, [])
  const adBlockAddSpot = useCallback((spot: AdBlockCatalogSpot) => {
    setAdBlockEntries(prev => [...prev, {
      spotId:         spot.id,
      title:          spot.title,
      durationSec:    spot.durationSec,
      campaignName:   spot.campaign?.name ?? null,
      advertiserName: spot.advertiser?.name ?? null,
      rowKey:         newAdBlockRowKey(),
    }])
    setAdBlockShowAdd(false)
    setAdBlockSearch('')
  }, [])

  const adBlockFilteredCatalog = useMemo(() => {
    if (!adBlockSearch.trim()) return adBlockCatalog
    const q = adBlockSearch.toLowerCase()
    return adBlockCatalog.filter(s =>
      s.title.toLowerCase().includes(q)
      || (s.campaign?.name ?? '').toLowerCase().includes(q)
      || (s.advertiser?.name ?? '').toLowerCase().includes(q),
    )
  }, [adBlockCatalog, adBlockSearch])

  /** Dans une série, l'ordre saison/épisode parle plus que l'ordre alphabétique. */
  const orderedPrograms = useMemo(() => {
    if (itemType !== 'PROGRAM' || scope !== 'SERIES') return programs
    return [...programs].sort((a, b) =>
      (a.season ?? 0) - (b.season ?? 0)
      || (a.episode ?? 0) - (b.episode ?? 0)
      || a.title.localeCompare(b.title, 'fr'),
    )
  }, [programs, itemType, scope])

  /** Le lot est choisi mais son contenant ne l'est pas encore. */
  const awaitingScopePick = itemType === 'PROGRAM'
    && ((scope === 'SERIES' && !scopeSeriesId) || (scope === 'COLLECTION' && !scopeCollId))

  const handleScopeChange = useCallback((next: ProgramScope) => {
    setScope(next)
    setProgSearch('')
    setProgQuery('')
  }, [])

  const toggleDow = useCallback((dow: number) => {
    setRecDaysOfWeek(prev =>
      prev.includes(dow) ? prev.filter(d => d !== dow) : [...prev, dow].sort()
    )
  }, [])

  const toggleDom = useCallback((dom: number) => {
    setRecDaysOfMonth(prev =>
      prev.includes(dom) ? prev.filter(d => d !== dom) : [...prev, dom].sort((a, b) => a - b)
    )
  }, [])

  // Résumé lisible de la récurrence
  const recSummary = useMemo(() => {
    if (!recEnabled) return ''
    const every = recInterval === 1 ? '' : ` toutes les ${recInterval}`
    if (recFreq === 'DAILY') return `Tous les jours${every} à ${time.slice(0,5)}`
    if (recFreq === 'WEEKLY') {
      const days = recDaysOfWeek.map(d => DAY_LABELS_LONG[d]).join(', ') || 'aucun jour'
      return `Chaque semaine${every} — ${days} à ${time.slice(0,5)}`
    }
    if (recFreq === 'MONTHLY') {
      const days = recDaysOfMonth.join(', ') || 'aucun jour'
      return `Chaque mois${every} — le ${days} à ${time.slice(0,5)}`
    }
    return ''
  }, [recEnabled, recFreq, recInterval, recDaysOfWeek, recDaysOfMonth, time])

  // Placeholder de rediff : un programme ad-hoc sans fichier vidéo encore disponible
  // peut être planifié quand même (l'opérateur attachera le fichier après diffusion).
  const _selectedRoot = programs.find(program => program.id === progId || program.parts?.some(part => part.id === progId))
  const _selectedProgram = _selectedRoot?.id === progId ? _selectedRoot : _selectedRoot?.parts?.find(part => part.id === progId)
  const isAdHocPlaceholder   = !!_selectedRoot?.adHoc && assets.length === 0
  const hasAllPartMedia = !!_selectedRoot && [_selectedRoot, ...(_selectedRoot.parts ?? [])]
    .every(part => part.mediaAssets?.some(asset => !asset.isProxy && isBroadcastableMedia(asset.status)))
  const canResolveProgramMedia = isAdHocPlaceholder || !!_selectedRoot?.programCode || hasAllPartMedia
  // Épisode à deux versions : deux montages étiquetés, sur un programme en une
  // seule partie. Le choix fixe le fichier ET la durée posée.
  const versionAssets = useMemo(
    () => (_selectedRoot?.parts?.length ? [] : assets
      .filter(asset => !asset.isProxy && asset.version)
      .sort((left, right) => (left.version === 'LONG' ? 0 : 1) - (right.version === 'LONG' ? 0 : 1))),
    [_selectedRoot?.parts?.length, assets],
  )
  const selectVersion = useCallback((asset: MediaAsset) => {
    setAssetId(asset.id)
    if (asset.durationSec) setDuration(asset.durationSec)
  }, [])
  const infoserviceRange = useMemo(
    () => infoserviceClockRange(date, infoserviceStartTime, infoserviceEndTime),
    [date, infoserviceEndTime, infoserviceStartTime],
  )

  // Le plan a été calculé sur une intersection précise. Déplacer l'horaire à la
  // main sort de cette intersection : l'ajout redevient un ajout ordinaire,
  // sans rien prendre à personne.
  const activePlan = useMemo(() => {
    // Le mode « trou » gère son propre plan (`gapPlan`) : ici, uniquement les
    // insertions à une intersection déjà collée.
    if (insertGap || !selectedInsertPlan || itemType === 'INFOSERVICE' || recEnabled) return null
    if (!date || !time || !duration || effectiveAnchorMode !== selectedInsertPlan.edge) return null
    const anchorMs = instantAtBroadcastClock(date, time).getTime()
    if (!Number.isFinite(anchorMs) || Math.abs(anchorMs - selectedInsertPlan.boundaryMs) > 1_000) return null
    return selectedInsertPlan
  }, [date, duration, effectiveAnchorMode, insertGap, itemType, recEnabled, selectedInsertPlan, time])

  const occupiedWithoutInsertion = enteredBoundary.occupied
    && effectiveInsertPlans.length === 0
    && !insertGap
    && !recEnabled
    && itemType !== 'INFOSERVICE'
  const occupiedInsertionMessage = canInsert
    ? 'Deux programmes occupent déjà cette frontière et aucun infoservice ajustable ne peut libérer la durée du jingle.'
    : 'Deux programmes occupent déjà cette frontière. Cette insertion nécessite le droit de déplacer les éléments de la grille.'

  useEffect(() => {
    if (choosablePlans.length === 0 || choosablePlans.some(plan => plan.edge === insertEdge)) return
    setInsertEdge(choosablePlans.find(plan => plan.edge === anchorMode)?.edge ?? choosablePlans[0].edge)
  }, [anchorMode, choosablePlans, insertEdge])

  // Ce que l'ajout coûtera à l'antenne déjà montée. Affiché avant validation :
  // raccourcir une plage et avancer une chaîne ne doit jamais être une
  // surprise, même quand c'est exactement ce que l'opérateur demande. Vaut aussi
  // pour le débordement d'un trou, qui ne rend que le surplus (`overflowSec`).
  const trimPreview = useMemo(() => {
    const plan = activePlan ?? gapPlan
    const removedSec = activePlan ? duration : overflowSec
    if (!plan || !removedSec) return null
    const trimsStart = plan.edge === 'START'
    const boundaryMs = new Date(
      trimsStart ? plan.trim.startTime : plan.trim.endTime,
    ).getTime() + (trimsStart ? removedSec : -removedSec) * 1_000
    return {
      edge: plan.edge,
      newBoundary: new Date(boundaryMs),
      removedSec,
      shifted: plan.shift.length,
      shiftedPrograms: plan.shift.filter(item => item.itemType === 'PROGRAM').length,
      tooShort: removedSec * 1_000 > plan.slackMs,
    }
  }, [activePlan, gapPlan, duration, overflowSec])

  // Débordement : si le côté retenu n'a pas la marge mais que l'autre l'a, on
  // bascule d'office. L'opérateur garde la main pour revenir en arrière.
  useEffect(() => {
    if (!gapMode || gapSideOk) return
    const feasible = choosablePlans.find(plan => gapSideFeasible(plan, overflowSec))
    if (feasible) setInsertEdge(feasible.edge)
  }, [gapMode, gapSideOk, choosablePlans, overflowSec])

  // Dans un trou, l'horaire affiché suit le bord qui ancre l'ajout : celui du
  // côté cédé en cas de débordement, le bord cliqué sinon. Sans ce recalage,
  // revenir d'un contenu trop long à un contenu qui tient laissait l'horaire
  // sur le bord opposé, et l'ajout se posait sur l'élément voisin.
  useEffect(() => {
    if (gapAnchorEdgeMs === null) return
    const d = new Date(gapAnchorEdgeMs)
    setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`)
    setTime(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`)
  }, [gapAnchorEdgeMs])

  const handleSubmit = useCallback(async () => {
    if (itemType === 'JINGLE' && occupiedWithoutInsertion) {
      setError(occupiedInsertionMessage)
      return
    }
    if (itemType === 'LIVE') {
      if (!adHocTitle.trim() || !adHocSourceId || !liveLogoProfileId || !date || !time || !duration) {
        setError('Titre, source live, habillage logo, date, heure et durée sont requis')
        return
      }
      if (livePartDurationsSec.some(value => !Number.isInteger(value) || value <= 0)) {
        setError('La durée de chaque partie doit être supérieure à zéro')
        return
      }
    } else if (itemType === 'AD_BLOCK') {
      if (adBlockEntries.length === 0 || !date || !time) {
        setError('Choisissez au moins un spot et un horaire')
        return
      }
    } else if (itemType === 'INFOSERVICE') {
      if (!infoserviceRange) {
        setError('Date, heure de début et heure de fin sont requises')
        return
      }
      if (!infoserviceResolution) {
        setError('Aucun infoservice sélectionné pour cette chaîne')
        return
      }
    } else {
      if (!progId || !date || !time || !duration) { setError('Programme, date, heure et durée sont requis'); return }
      // assetId est optionnel pour les programmes ad-hoc sans fichier (placeholder de rediff)
      if (!assetId && !canResolveProgramMedia) {
        setError('Associez un média, ou renseignez un code programme permettant la recherche automatique.')
        return
      }
    }

    setLoading(true)
    setError('')
    try {
      const targetGrid = await ensureTargetGrid()
      // Photo de la semaine avant l'ajout : c'est elle qui permettra au bouton
      // « Annuler » de retirer exactement ce qui va être posé. Un bloc
      // multipartie, une salve de spots ou une plage d'infoservice sont un seul
      // geste pour l'opérateur et doivent se défaire d'un seul clic, quel que
      // soit le nombre d'éléments que le serveur aura créés.
      //
      // La récurrence reste en dehors : elle sème des occurrences sur des
      // semaines, voire des mois, là où la photo ne couvre qu'une semaine —
      // proposer d'annuler n'en retirerait qu'une partie, en silence.
      const recording = recEnabled ? null : await beginGridChange({
        label:   ADD_UNDO_LABELS[itemType] ?? 'ajout à la grille',
        gridIds: [targetGrid],
        date,
      })
      // La saisie est une heure d'antenne, pas une heure de poste : c'est elle
      // que la regie annonce et que le serveur declenchera. La resoudre avec le
      // fuseau du navigateur decalait toute la programmation d'un operateur en
      // deplacement.
      let startTimeMs: number
      if (itemType === 'INFOSERVICE' && infoserviceRange) {
        startTimeMs = infoserviceRange.start.getTime()
      } else if (gapMode && gapPlan) {
        // L'ajout occupe tout le trou et déborde du côté cédé : son emplacement
        // découle du trou, pas de l'horaire affiché.
        startTimeMs = gapOverflowPlacement(insertGap!, gapPlan.edge, duration).startMs
      } else {
        startTimeMs = instantAtBroadcastClock(date, time).getTime()
        if (effectiveAnchorMode === 'END') startTimeMs -= duration * 1000
      }

      // ── Place prise sur l'infoservice voisin ────────────────────────────
      // Il cède exactement ce que l'ajout occupe, par le bord tourné vers
      // l'intersection. La photo « avant » est déjà prise : annuler l'ajout
      // rend aussi ses secondes à l'infoservice et remet l'habillage à l'heure.
      if (activePlan) {
        if (duration * 1_000 > activePlan.slackMs) {
          setError('L’infoservice voisin est trop court pour libérer cette durée.')
          setLoading(false)
          return
        }
        // Le bloc pub libère et occupe sa place dans une transaction serveur
        // unique. Les autres types gardent le flux historique en deux appels.
        if (itemType !== 'AD_BLOCK') await openInsertionSpace(activePlan, duration)
      }

      // ── Débordement d'un trou : seul le surplus est rendu ────────────────
      // On comble le trou pour rien, puis on rogne le côté choisi de la seule
      // durée qui dépasse. Refus net si cette plage n'a pas la marge.
      if (gapMode) {
        if (!gapPlan || !gapSideOk) {
          setError('Aucun infoservice voisin ne peut absorber le débordement de ce côté.')
          setLoading(false)
          return
        }
        if (itemType !== 'AD_BLOCK') await openInsertionSpace(gapPlan, overflowSec)
      }

      // ── Mode AD_HOC : appel dédié qui crée programme + item en une transaction ──
      if (itemType === 'LIVE') {
        const liveResult = await programsApi.createAdHocLive({
          channelId,
          gridId:       targetGrid,
          title:        adHocTitle.trim(),
          liveSourceId: adHocSourceId,
          liveStartMode,
          liveEndMode,
          liveHoldClipId: liveStartMode === 'MANUAL' ? liveHoldClipId || null : null,
          startTime:    new Date(startTimeMs).toISOString(),
          durationSec:  livePartDurationsSec[0],
          partDurationsSec: livePartDurationsSec,
          interPartBreakSec: liveInterPartBreakSec,
          createReplayProgram,
          titleShort:   liveTitleShort.trim() || undefined,
          epgSubtitle:  liveSubtitle.trim() || undefined,
          summary:      liveSummary.trim() || undefined,
          genre:        liveGenre.trim() || undefined,
          tags:         liveTags.split(',').map(t => t.trim()).filter(Boolean),
          director:     liveDirector.trim() || undefined,
          presenter:    livePresenter.trim() || undefined,
          production:   liveProduction.trim() || undefined,
          year:         liveYear ? Number(liveYear) : undefined,
          isNew:        liveIsNew,
          epgFederation: liveFederation.trim() || undefined,
          epgThemeId:   liveThemeId ? Number(liveThemeId) : undefined,
          epgShowId:    liveShowId  ? Number(liveShowId)  : undefined,
          logoProfileId: liveLogoProfileId,
        })
        // Même sécurité qu'un programme : la pose passe, mais les voisins
        // recouverts sont mis en évidence pour que l'opérateur voie le
        // chevauchement au lieu d'un simple refus.
        const liveConflicts = liveResult?.conflicts ?? []
        if (liveConflicts.length > 0) {
          useStore.getState().setConflicts(liveConflicts.map((c: { id: string }) => c.id))
        }
        // Heure déjà passée : le serveur a recalé le départ à maintenant + 3 s,
        // sans quoi le moteur ne l'aurait jamais lancé.
        if ((liveResult?.startShiftedSec ?? 0) > 0) {
          useStore.getState().pushToast(
            `Heure de départ déjà passée : le direct part à ${formatTime(liveResult.item.startTime)} (dans 3 s).`,
            'warning',
          )
        }
        if (recording) await commitGridChange(recording)
        onCreated()
        onClose()
        return
      }

      // ── Mode AD_BLOCK : insertion d'un bloc pub via /ad-blocks/apply ──
      if (itemType === 'AD_BLOCK') {
        let cursor = startTimeMs
        const spots = adBlockEntries.map(e => {
          const spot = {
            spotId:      e.spotId,
            startTime:   new Date(cursor).toISOString(),
            durationSec: e.durationSec,
          }
          cursor += (e.durationSec + AD_BLOCK_BLACKSCREEN_SEC) * 1000
          return spot
        })
        // Une insertion en force rend la durée entière ; un débordement de trou,
        // seulement le surplus. Dans les deux cas la place s'ouvre et se comble
        // dans la même transaction serveur.
        const blockInsertion = activePlan
          ? insertionSpaceRequest(activePlan, duration)
          : gapMode && gapPlan
            ? insertionSpaceRequest(gapPlan, overflowSec)
            : null
        if (blockInsertion) {
          await adBlocksApi.applyWithInsertion({
            channelId,
            gridId: targetGrid,
            spots,
            insertion: blockInsertion,
          })
        } else {
          await adBlocksApi.apply({ channelId, gridId: targetGrid, spots })
        }
        if (recording) await commitGridChange(recording)
        if (activePlan) onInserted?.({
          plan: activePlan,
          gridId: targetGrid,
          recipe: {
            kind: 'AD_BLOCK',
            spots: adBlockEntries.map(entry => ({ spotId: entry.spotId, durationSec: entry.durationSec })),
            durationSec: duration,
            label: `bloc de ${adBlockEntries.length} spot(s)`,
          },
        })
        onCreated()
        onClose()
        return
      }

      if (itemType === 'INFOSERVICE') {
        await infoservicesApi.fillGaps({
          channelId,
          gridId: targetGrid,
          date,
          from: infoserviceRange!.start.toISOString(),
          to: infoserviceRange!.end.toISOString(),
        })
        if (recording) await commitGridChange(recording)
        onCreated()
        onClose()
        return
      }

      if (recEnabled) {
        // ── Création avec récurrence ──
        if (recFreq === 'WEEKLY' && recDaysOfWeek.length === 0) {
          setError('Sélectionnez au moins un jour de la semaine')
          setLoading(false)
          return
        }
        if (recFreq === 'MONTHLY' && recDaysOfMonth.length === 0) {
          setError('Sélectionnez au moins un jour du mois')
          setLoading(false)
          return
        }

        const payload: any = {
          channelId,
          gridId:       targetGrid,
          itemType:     itemType === 'JINGLE' ? 'JINGLE' : 'PROGRAM',
          durationSec:  duration,
          frequency:    recFreq,
          interval:     recInterval,
          daysOfWeek:   recDaysOfWeek,
          daysOfMonth:  recDaysOfMonth,
          timeOfDay:    time.length === 5 ? time + ':00' : time,
          startDate:    date,
        }
        payload.programId = progId
        if (assetId) payload.mediaAssetId = assetId   // omis pour les placeholders ad-hoc
        if (recEndMode === 'date' && recEndDate)  payload.endDate  = recEndDate
        if (recEndMode === 'count' && recCount > 0) payload.maxCount = recCount

        const { count } = await recurrenceApi.create(payload)
        if (recording) await commitGridChange(recording)
        onCreated()
        onClose()
        return
      }

      // ── Création unitaire (sans récurrence) ──
      const startTime = new Date(startTimeMs).toISOString()
      const payload: Record<string, unknown> = {
        gridId:      targetGrid,
        startTime,
        endTime:     new Date(startTimeMs + duration * 1000).toISOString(),
        durationSec: duration,
        itemType,
        sortOrder:   Math.floor(startTimeMs / 1000),
      }
      payload.programId = progId
      if (assetId) payload.mediaAssetId = assetId   // omis pour les placeholders ad-hoc

      await itemsApi.create(payload)
      if (recording) await commitGridChange(recording)
      if (activePlan) onInserted?.({
        plan: activePlan,
        gridId: targetGrid,
        recipe: {
          kind: 'ITEM',
          itemType: itemType === 'JINGLE' ? 'JINGLE' : 'PROGRAM',
          programId: progId,
          mediaAssetId: assetId || undefined,
          durationSec: duration,
          label: _selectedProgram?.title ?? 'cet élément',
        },
      })
      onCreated()
      onClose()
    } catch (err: any) {
      setError(err.message ?? 'Erreur lors de la création')
    } finally {
      setLoading(false)
    }
  }, [
    itemType, progId, assetId, date, time, duration, infoserviceRange, occupiedInsertionMessage, occupiedWithoutInsertion,
    ensureTargetGrid, channelId, effectiveAnchorMode, activePlan, onInserted, _selectedProgram,
    insertGap, gapMode, gapPlan, gapSideOk, overflowSec,
    recEnabled, recFreq, recInterval, recDaysOfWeek, recDaysOfMonth,
    recEndMode, recEndDate, recCount,
    adHocTitle, adHocSourceId, createReplayProgram, liveTitleShort, liveSubtitle, liveSummary,
    livePartDurationsSec, liveInterPartBreakSec,
    liveGenre, liveTags, liveDirector, livePresenter, liveProduction, liveYear, liveIsNew, liveLogoProfileId,
    liveStartMode, liveEndMode, liveHoldClipId,
    liveFederation, liveThemeId, liveShowId,
    adBlockEntries, infoserviceResolution,
    isAdHocPlaceholder,
    onCreated, onClose,
  ])

  const fmtDuration = (sec: number) => {
    const h = Math.floor(sec / 3600)
    const m = Math.floor((sec % 3600) / 60)
    const s = sec % 60
    if (h > 0) return `${h}h${String(m).padStart(2,'0')}m${String(s).padStart(2,'0')}s`
    return `${m}m${String(s).padStart(2,'0')}s`
  }

  // Placeholder de rediff : déjà calculé plus haut (avant handleSubmit).
  const selectedProgram = _selectedProgram

  const canSubmit = itemType === 'LIVE'
    ? !!adHocTitle.trim() && !!adHocSourceId && !!liveLogoProfileId && !!duration
    : itemType === 'AD_BLOCK'
      ? adBlockEntries.length > 0 && !!date && !!time
      : itemType === 'INFOSERVICE'
        ? !!infoserviceRange && !!infoserviceResolution
      : !!progId && (!!assetId || canResolveProgramMedia) && !!duration

  return (
    <ModalShell className="agenda-editor add-item-editor" onClose={onClose} size="wide" title="Programmer">
      <div className="add-item-editor__modes" aria-label="Type de programmation">
        {MODE_DEFS.map(mode => (
          <ModeButton
            key={mode.id}
            active={itemType === mode.id}
            mode={mode}
            onSelect={setItemType}
          />
        ))}
      </div>

      <div className="add-item-editor__body">
        <div className="add-item-editor__fields">

            {/* ── AD_BLOCK : composition d'un bloc publicitaire (multi-spots) ── */}
            {itemType === 'AD_BLOCK' && (
              <>
                <Field label={`Spots du bloc (${adBlockEntries.length})`}>
                  {adBlockEntries.length === 0 ? (
                    <div className="agenda-editor__empty agenda-editor__empty--outlined">
                      Aucun spot ajouté. Cliquez sur « Ajouter un spot » pour composer votre bloc.
                    </div>
                  ) : (
                    <DndContext
                      sensors={adBlockSensors}
                      collisionDetection={closestCenter}
                      modifiers={[restrictToVerticalAxis]}
                      onDragEnd={adBlockHandleDragEnd}
                    >
                      <SortableContext items={adBlockEntries.map(e => e.rowKey)} strategy={verticalListSortingStrategy}>
                        <div className="ad-spot-list">
                          {adBlockEntries.map((e, idx) => (
                            <AdBlockSpotRow
                              key={e.rowKey}
                              entry={e}
                              index={idx}
                              isLast={idx === adBlockEntries.length - 1}
                              onMoveUp={() => adBlockMoveSpot(idx, -1)}
                              onMoveDown={() => adBlockMoveSpot(idx, 1)}
                              onRemove={() => adBlockRemoveSpot(e.rowKey)}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  )}
                </Field>

                {!adBlockShowAdd && !loadingP && (
                  <button
                    type="button"
                    onClick={() => setAdBlockShowAdd(true)}
                    className="ad-spot-add-trigger"
                  >
                    + Ajouter un spot
                  </button>
                )}

                {adBlockShowAdd && (
                  <AdBlockAddPanel
                    catalog={adBlockFilteredCatalog}
                    search={adBlockSearch}
                    onSearchChange={setAdBlockSearch}
                    onPick={adBlockAddSpot}
                    onCancel={() => { setAdBlockShowAdd(false); setAdBlockSearch('') }}
                    loading={loadingP}
                  />
                )}

                {adBlockEntries.length > 0 && (
                  <div className="add-item-editor__notice is-ad">
                    Durée totale calculée : <strong className="agenda-editor__mono">{fmtDuration(adBlockDurationSec)}</strong>.
                    Le bloc sera ajouté à la grille comme un seul élément, modifiable jusqu'à diffusion.
                  </div>
                )}
              </>
            )}

            {/* ── AD_HOC : direct créé inline (titre + source) ── */}
            {itemType === 'LIVE' && (
              <>
                <Field label="Titre du direct">
                  <input
                    type="text"
                    value={adHocTitle}
                    onChange={e => setAdHocTitle(e.target.value)}
                    placeholder="Ex : Journal local 19h"
                    autoFocus
                    className="tp-input"
                  />
                </Field>

                <label className={`add-item-editor__toggle-card${createReplayProgram ? ' is-selected' : ''}`}>
                  <input
                    type="checkbox"
                    checked={createReplayProgram}
                    onChange={event => {
                      setCreateReplayProgram(event.target.checked)
                      if (!event.target.checked) {
                        setLivePartDurationsMin(parts => [parts[0] ?? '60'])
                        setLiveInterPartBreakMin('0')
                      }
                    }}
                  />
                  <div className="add-item-editor__toggle-copy">
                    <div className="add-item-editor__toggle-title">
                      Creer un programme pour rediffusion
                    </div>
                    <div className="add-item-editor__toggle-description">
                      Le direct sera lie a une fiche programme et pourra recevoir un fichier apres diffusion.
                    </div>
                  </div>
                </label>

                {createReplayProgram && (
                  <div className="tp-form-panel">
                    <div className="tp-form-grid">
                      <Field label="Nombre de parties">
                        <input
                          type="number"
                          min={1}
                          max={20}
                          aria-label="Nombre de parties du direct"
                          value={livePartDurationsMin.length}
                          onChange={event => {
                            const count = Math.min(20, Math.max(1, Number(event.target.value)))
                            setLivePartDurationsMin(parts => Array.from(
                              { length: count },
                              (_, index) => parts[index] ?? parts[0] ?? '60',
                            ))
                          }}
                          className="tp-input"
                        />
                      </Field>
                      <Field label="Coupure entre parties (minutes)">
                        <input
                          type="number"
                          min={0}
                          step={0.5}
                          aria-label="Coupure entre les parties en minutes"
                          value={liveInterPartBreakMin}
                          onChange={event => setLiveInterPartBreakMin(event.target.value)}
                          className="tp-input"
                        />
                      </Field>
                    </div>
                    <div className="tp-form-grid tp-form-grid--three catalog-part-grid">
                      {livePartDurationsMin.map((value, index) => (
                        <Field key={index} label={`Partie ${index + 1} (minutes)`}>
                          <input
                            type="number"
                            min={0.5}
                            step={0.5}
                            aria-label={`Durée de la partie ${index + 1} en minutes`}
                            value={value}
                            onChange={event => setLivePartDurationsMin(parts => parts.map(
                              (part, partIndex) => partIndex === index ? event.target.value : part,
                            ))}
                            className="tp-input"
                          />
                        </Field>
                      ))}
                    </div>
                    <div className="add-item-editor__notice is-info">
                      Le direct est posé en un seul bloc, coupures comprises : ce découpage est le déroulé annoncé, pour le chrono de régie et l’EPG. Vous décrochez quand vous le décidez pendant l’émission.
                    </div>
                  </div>
                )}

                <div className="add-item-editor__two-columns">
                  <Field label="Titre court EPG">
                    <input type="text" value={liveTitleShort} onChange={e => setLiveTitleShort(e.target.value)} placeholder="Optionnel" className="tp-input" />
                  </Field>
                  <Field label="Genre">
                    <input type="text" value={liveGenre} onChange={e => setLiveGenre(e.target.value)} placeholder="Magazine, sport..." className="tp-input" />
                  </Field>
                </div>

                <Field label="Sous-titre EPG">
                  <input
                    type="text"
                    value={liveSubtitle}
                    onChange={e => setLiveSubtitle(e.target.value)}
                    placeholder="Sous-titre publié dans l'EPG (optionnel)"
                    className="tp-input"
                  />
                </Field>

                <Field label="Resume EPG">
                  <textarea
                    value={liveSummary}
                    onChange={e => setLiveSummary(e.target.value)}
                    placeholder="Resume affiche dans l'EPG"
                    rows={3}
                    className="tp-textarea"
                  />
                </Field>

                <Field label="Tags EPG">
                  <input type="text" value={liveTags} onChange={e => setLiveTags(e.target.value)} placeholder="Separés par des virgules" className="tp-input" />
                </Field>

                {/* Nomenclature Plurimedia : sans elle, le direct part au partenaire
                    sans rubrique ni rattachement à son émission. */}
                <div className="add-item-editor__two-columns">
                  <Field label="Fédération EPG">
                    <input type="text" value={liveFederation} onChange={e => setLiveFederation(e.target.value)} placeholder="Optionnel" className="tp-input" />
                  </Field>
                  <Field label="Identifiant de thème">
                    <input type="number" min={0} value={liveThemeId} onChange={e => setLiveThemeId(e.target.value)} placeholder="Fourni par Plurimedia" className="tp-input" />
                  </Field>
                </div>

                <Field label="Identifiant d’émission">
                  <input type="number" min={0} value={liveShowId} onChange={e => setLiveShowId(e.target.value)} placeholder="Fourni par Plurimedia" className="tp-input" />
                </Field>

                <div className="add-item-editor__two-columns">
                  <Field label="Realisateur">
                    <input type="text" value={liveDirector} onChange={e => setLiveDirector(e.target.value)} placeholder="Optionnel" className="tp-input" />
                  </Field>
                  <Field label="Presentateur">
                    <input type="text" value={livePresenter} onChange={e => setLivePresenter(e.target.value)} placeholder="Optionnel" className="tp-input" />
                  </Field>
                </div>

                <div className="add-item-editor__two-columns">
                  <Field label="Production">
                    <input type="text" value={liveProduction} onChange={e => setLiveProduction(e.target.value)} placeholder="Optionnel" className="tp-input" />
                  </Field>
                  <Field label="Annee">
                    <input type="number" min={1900} max={2100} value={liveYear} onChange={e => setLiveYear(e.target.value)} placeholder="Optionnel" className="tp-input" />
                  </Field>
                </div>

                <div className="add-item-editor__two-columns">
                  <Field label="Logo antenne">
                    <select value={liveLogoProfileId} onChange={e => setLiveLogoProfileId(e.target.value)} className="tp-select">
                      {logoProfiles.filter(profile => profile.active).map(profile => (
                        <option key={profile.id} value={profile.id}>{profile.name}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Statut EPG">
                    <label className="tp-input add-item-editor__checkbox-field">
                      <input type="checkbox" checked={liveIsNew} onChange={e => setLiveIsNew(e.target.checked)} />
                      Inedit
                    </label>
                  </Field>
                </div>

                <Field label="Source live">
                  {loadingP ? (
                    <div className="agenda-editor__loading"><Spinner /> Chargement…</div>
                  ) : liveSources.length === 0 ? (
                    <div className="add-item-editor__notice is-warning">
                      ⚠ Aucune source live configurée. Ajoutez-en dans Réglages → Sources live.
                    </div>
                  ) : (
                    <select value={adHocSourceId} onChange={e => setAdHocSourceId(e.target.value)} className="tp-select">
                      <option value="">— Choisir une source —</option>
                      {liveSources.map(s => (
                        <option key={s.id} value={s.id}>{s.name} ({s.sourceType === 'DECKLINK' ? 'Decklink' : 'Internet'})</option>
                      ))}
                    </select>
                  )}
                </Field>

                <div className="add-item-editor__two-columns">
                  <Field label="Lancement du direct">
                    <select value={liveStartMode} onChange={e => setLiveStartMode(e.target.value as LiveStartMode)} className="tp-select">
                      <option value="AUTO">Automatique à l'heure prévue</option>
                      <option value="MANUAL">Manuel par l'opérateur</option>
                    </select>
                  </Field>
                  {liveStartMode === 'MANUAL' && (
                    <Field label="Vidéo d'attente">
                      <select value={liveHoldClipId} onChange={e => setLiveHoldClipId(e.target.value)} className="tp-select">
                        <option value="">Interlude par défaut de la chaîne</option>
                        {interludeClips.map(clip => <option key={clip.id} value={clip.id}>{clip.name}</option>)}
                      </select>
                    </Field>
                  )}
                </div>

                {liveStartMode === 'MANUAL' && (
                  <div className="add-item-editor__notice is-warning">
                    À l'heure prévue, la vidéo d'attente tournera en boucle jusqu'au clic sur « Passer au direct ».
                  </div>
                )}

                <div className="add-item-editor__two-columns">
                  <Field label="Fin du direct">
                    <select value={liveEndMode} onChange={e => setLiveEndMode(e.target.value as LiveEndMode)} className="tp-select">
                      <option value="AUTO">Automatique à l'heure prévue</option>
                      <option value="MANUAL">Manuel par l'opérateur</option>
                    </select>
                  </Field>
                </div>

                {liveEndMode === 'MANUAL' && (
                  <div className="add-item-editor__notice is-warning">
                    Pas d'alerte de fin : l'opérateur coupe via « Fin du direct ». Le trou est comblé par de l'infoservice (&gt; 1 min) ou de l'interlude, sans décaler la suite. Filet de sécurité : coupe au prochain programme.
                  </div>
                )}

                <div className="add-item-editor__notice is-ad">
                  Ce direct sera ajoute a la grille depuis sa source live. Si la case rediffusion est cochee, les champs EPG resteront synchronises avec la fiche programme liee.
                </div>
              </>
            )}


            {/* ── PROGRAM : choix du lot où piocher (série, collection, unitaires) ── */}
            {itemType === 'PROGRAM' && (
              <>
                <Field label="Où piocher">
                  <div className="add-item-scope" role="group" aria-label="Lot de programmes">
                    {SCOPE_DEFS.map(def => (
                      <ScopeButton
                        key={def.id}
                        def={def}
                        active={scope === def.id}
                        onSelect={handleScopeChange}
                      />
                    ))}
                  </div>
                </Field>

                {scope === 'SERIES' && (
                  <Field label="Série">
                    {loadingScope ? (
                      <div className="agenda-editor__loading"><Spinner /> Chargement…</div>
                    ) : seriesList.length === 0 ? (
                      <div className="add-item-editor__notice is-warning">
                        ⚠ Aucune série au catalogue. Créez-en une dans Programmes → Séries.
                      </div>
                    ) : (
                      <select
                        value={scopeSeriesId}
                        onChange={e => { setScopeSeriesId(e.target.value); setProgSearch(''); setProgQuery('') }}
                        className="tp-select"
                      >
                        <option value="">— Choisir une série —</option>
                        {seriesList.map(s => (
                          <option key={s.id} value={s.id}>
                            {s.title}{s._count ? ` · ${s._count.programs} épisode${s._count.programs > 1 ? 's' : ''}` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                )}

                {scope === 'COLLECTION' && (
                  <Field label="Collection">
                    {loadingScope ? (
                      <div className="agenda-editor__loading"><Spinner /> Chargement…</div>
                    ) : collections.length === 0 ? (
                      <div className="add-item-editor__notice is-warning">
                        ⚠ Aucune collection au catalogue. Créez-en une dans Programmes → Collections.
                      </div>
                    ) : (
                      <select
                        value={scopeCollId}
                        onChange={e => { setScopeCollId(e.target.value); setProgSearch(''); setProgQuery('') }}
                        className="tp-select"
                      >
                        <option value="">— Choisir une collection —</option>
                        {collections.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.name}{c._count ? ` · ${c._count.programs} programme${c._count.programs > 1 ? 's' : ''}` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                )}
              </>
            )}

            {/* ── PROGRAM / JINGLE : sélecteur programme ── */}
            {(itemType === 'PROGRAM' || itemType === 'JINGLE') && (
              <>
                <Field label="Programme">
                  {awaitingScopePick ? (
                    <div className="agenda-editor__empty agenda-editor__empty--outlined">
                      {scope === 'SERIES'
                        ? 'Choisissez d’abord une série pour voir ses épisodes.'
                        : 'Choisissez d’abord une collection pour voir son contenu.'}
                    </div>
                  ) : (
                    <div className="add-item-editor__program-picker">
                      {itemType === 'PROGRAM' && (
                        <input
                          type="search"
                          value={progSearch}
                          onChange={e => setProgSearch(e.target.value)}
                          placeholder="Filtrer par titre, résumé, genre…"
                          className="tp-input add-item-editor__program-search"
                        />
                      )}
                      {loadingP ? (
                        <div className="agenda-editor__loading"><Spinner /> Chargement…</div>
                      ) : (
                        <select value={progId} onChange={e => { setProgId(e.target.value); setAssetId('') }} className="tp-select">
                          <option value="">— Choisir un programme —</option>
                          {orderedPrograms.flatMap(program => [
                            <option key={program.id} value={program.id}>{program.adHoc ? '🔴 ad-hoc · ' : ''}{episodePrefix(program)}{program.title} ({fmtDuration(program.blockDurationSec ?? program.durationSec)}{(program.partCount ?? 1) > 1 ? ` · ${program.partCount} parties` : ''})</option>,
                            ...(program.parts ?? []).map(part => <option key={part.id} value={part.id}>↳ Partie {part.partNumber} · {part.title}</option>),
                          ])}
                        </select>
                      )}
                      {!loadingP && orderedPrograms.length === 0 && (
                        <div className="add-item-editor__notice is-warning">
                          ⚠ Aucun programme diffusable {progQuery ? 'pour cette recherche' : itemType === 'JINGLE' ? 'au catalogue' : 'dans ce lot'}.
                        </div>
                      )}
                      {progTruncated && (
                        <div className="add-item-editor__notice is-info">
                          Liste limitée aux 100 premiers titres — affinez avec le champ de recherche.
                        </div>
                      )}
                    </div>
                  )}
                </Field>
                {itemType === 'PROGRAM' && scope === 'UNGROUPED' && (
                  <AdHocToggle checked={includeAdHoc} onChange={setIncludeAdHoc} />
                )}
              </>
            )}

            {/* Fichier média (PROGRAM/JINGLE seulement) */}
            {(itemType === 'PROGRAM' || itemType === 'JINGLE') && progId && (
              versionAssets.length > 1 ? (
                <Field label="Version">
                  <div className="add-item-scope" role="radiogroup" aria-label="Version de l’épisode">
                    {versionAssets.map(asset => (
                      <VersionChoice
                        key={asset.id}
                        asset={asset}
                        active={asset.id === assetId}
                        label={`${asset.version === 'LONG' ? 'Longue' : 'Courte'}${asset.durationSec ? ` · ${fmtDuration(asset.durationSec)}` : ''}`}
                        onSelect={selectVersion}
                      />
                    ))}
                  </div>
                </Field>
              ) : assets.length > 0 ? (
                <Field label="Fichier média">
                  <select value={assetId} onChange={e => {
                    setAssetId(e.target.value)
                    const a = assets.find(x => x.id === e.target.value)
                    if (a?.durationSec && !(_selectedRoot?.parts?.length)) setDuration(a.durationSec as unknown as number)
                  }} className="tp-select">
                    {assets.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.fileName}{a.durationSec ? ` · ${fmtDuration(a.durationSec as unknown as number)}` : ''}{a.isProxy ? ' (proxy)' : ''}
                      </option>
                    ))}
                  </select>
                </Field>
              ) : isAdHocPlaceholder ? (
                <div className="add-item-editor__notice is-ad">
                  Placeholder de rediffusion : ce direct ad-hoc n'a pas encore de fichier vidéo.
                  L'item sera ajouté à la grille comme réservation. Une fois le fichier d'enregistrement disponible,
                  attachez-le au programme via la fiche programme — il sera diffusé à l'horaire prévu.
                </div>
              ) : (
                <div className="add-item-editor__notice is-warning">
                  ⚠ Ce programme n'a pas encore de fichier vidéo. Ajoutez-en un dans la fiche du programme avant de planifier la rediffusion.
                </div>
              )
            )}

            {itemType !== 'LIVE' && progId && assets.length === 0 && (
              <div className="add-item-editor__notice is-warning">
                ⚠ Aucun fichier média associé à ce programme.
              </div>
            )}

            {itemType === 'INFOSERVICE' && (
              <div className={`add-item-editor__notice ${infoserviceResolution ? 'is-success' : 'is-warning'}`}>
                {infoserviceResolution
                  ? `${infoserviceResolution.fileName}${infoserviceResolution.mediaDate ? ` · ${infoserviceResolution.mediaDate}` : ''} · sélection active`
                  : loadingP ? 'Chargement de l’infoservice…' : 'Aucun infoservice sélectionné · choisissez-le dans Régie pub → Infoservice'}
              </div>
            )}

            {itemType !== 'INFOSERVICE' && !recEnabled
              && (insertGap ? gapMode && choosablePlans.length >= 1 : effectiveInsertPlans.length > 1) && (
              <fieldset className="add-item-editor__insert-source">
                <legend>{gapMode ? `Rendre le débordement (${fmtDuration(overflowSec)}) sur` : 'Prendre le temps sur'}</legend>
                <div className="add-item-editor__insert-options">
                  {choosablePlans.map(plan => {
                    const programCount = plan.shift.filter(entry => entry.itemType === 'PROGRAM').length
                    const otherCount = plan.shift.length - programCount
                    const direction = plan.edge === 'END' ? 'avancé' : 'retardé'
                    const detail = [
                      programCount > 0 ? `${programCount} programme${programCount > 1 ? 's' : ''}` : '',
                      otherCount > 0 ? `${otherCount} autre${otherCount > 1 ? 's' : ''} élément${otherCount > 1 ? 's' : ''}` : '',
                    ].filter(Boolean).join(' et ')
                    const runSec = Math.round((new Date(plan.trim.endTime).getTime() - new Date(plan.trim.startTime).getTime()) / 1_000)
                    const cedableSec = Math.floor(plan.slackMs / 1_000)
                    // En mode trou, seul le surplus est prélevé : un côté sans la
                    // marge nécessaire est proposé mais barré.
                    const blocked = gapMode && !gapSideFeasible(plan, overflowSec)
                    return (
                      <label
                        key={plan.edge}
                        className={`add-item-editor__toggle-card${insertEdge === plan.edge ? ' is-selected' : ''}${blocked ? ' is-disabled' : ''}`}
                      >
                        <input
                          type="radio"
                          name="insert-source"
                          value={plan.edge}
                          checked={insertEdge === plan.edge}
                          disabled={blocked}
                          onChange={() => setInsertEdge(plan.edge)}
                        />
                        <span className="add-item-editor__toggle-copy">
                          <span className="add-item-editor__toggle-title">
                            Infoservice du {plan.edge === 'END' ? 'haut' : 'bas'}
                          </span>
                          <span className="add-item-editor__toggle-duration">
                            Plage {fmtDuration(runSec)} · peut céder {fmtDuration(cedableSec)}
                          </span>
                          <span className={`add-item-editor__toggle-description${blocked ? ' is-blocked' : ''}`}>
                            {blocked
                              ? `Trop court : ${fmtDuration(overflowSec)} manquent pour absorber le débordement`
                              : detail
                                ? `${detail} ${direction}${programCount + otherCount > 1 ? 's' : ''} de ${gapMode ? 'l’excédent' : 'la durée ajoutée'}`
                                : 'Aucun autre élément ne sera décalé'}
                          </span>
                        </span>
                      </label>
                    )
                  })}
                </div>
              </fieldset>
            )}

            {/* Date + Heure */}
            <div className="add-item-editor__schedule">
              {itemType === 'INFOSERVICE' ? (
                <>
                  <Field label="Date de début">
                    <input type="date" value={date} onChange={e => setDate(e.target.value)} className="tp-input" />
                  </Field>
                  <div className="add-item-editor__two-columns">
                    <Field label="Heure de début">
                      <input type="time" step="1" value={infoserviceStartTime} onChange={e => setInfoserviceStartTime(e.target.value)} className="tp-input" />
                    </Field>
                    <Field label="Heure de fin">
                      <input type="time" step="1" value={infoserviceEndTime} onChange={e => setInfoserviceEndTime(e.target.value)} className="tp-input" />
                    </Field>
                  </div>
                  {infoserviceRange && broadcastDayKey(infoserviceRange.end) !== date && (
                    <div className="add-item-editor__start-preview">↳ Fin le lendemain à {infoserviceEndTime.slice(0, 5)}</div>
                  )}
                </>
              ) : (
                <div className="add-item-editor__two-columns">
                  <Field label={recEnabled ? 'Date de début' : 'Date de diffusion'}>
                    <input type="date" value={date} onChange={e => setDate(e.target.value)} className="tp-input" />
                  </Field>
                  <Field label={effectiveAnchorMode === 'END' ? 'Heure de fin (locale)' : 'Heure de début (locale)'}>
                    <input type="time" step="1" value={time} onChange={e => setTime(e.target.value)} className="tp-input" />
                  </Field>
                </div>
              )}
              {itemType !== 'INFOSERVICE' && effectiveAnchorMode === 'END' && duration > 0 && (
                <div className="add-item-editor__start-preview">
                  ↳ Débutera à : {(() => {
                    const [y, mo, d]   = date.split('-').map(Number)
                    const [h, mi, sec] = time.split(':').map(Number)
                    if (!y || isNaN(h)) return '...'
                    const dObj = new Date(new Date(y, mo - 1, d, h, mi, sec ?? 0, 0).getTime() - duration * 1000)
                    if (isNaN(dObj.getTime())) return '...'
                    return dObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                  })()}
                </div>
              )}
              {gapMode && (
                <div className="add-item-editor__start-preview">
                  ↳ L’élément comble le trou puis déborde de {fmtDuration(overflowSec)}, rendus par l’infoservice du {insertEdge === 'END' ? 'haut' : 'bas'}.
                </div>
              )}
              {gapMode && !gapSideOk && (
                <div className="add-item-editor__start-preview is-warning">
                  ⚠ Aucun infoservice voisin ne peut absorber ce débordement.
                </div>
              )}
              {trimPreview && (
                <div className={`add-item-editor__start-preview${trimPreview.tooShort ? ' is-warning' : ''}`}>
                  {trimPreview.tooShort
                    ? '⚠ L’infoservice voisin est trop court pour libérer cette durée.'
                    : `↳ L’infoservice sera raccourci de ${fmtDuration(trimPreview.removedSec)} (${trimPreview.edge === 'START' ? 'début' : 'fin'} à ${trimPreview.newBoundary.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })})${trimPreview.shiftedPrograms > 0 ? ` · ${trimPreview.shiftedPrograms} programme(s) décalé(s) d’autant` : trimPreview.shifted > 0 ? ` · ${trimPreview.shifted} élément(s) décalé(s) d’autant` : ''}`}
                </div>
              )}
              {itemType === 'JINGLE' && occupiedWithoutInsertion && (
                <div className="add-item-editor__start-preview is-warning" role="alert">
                  {occupiedInsertionMessage}
                </div>
              )}
              {itemType !== 'INFOSERVICE' && lastItem && effectiveAnchorMode !== 'END' && !recEnabled && (
                <button onClick={snapToAfterLast}
                  title="Positionner juste après le dernier programme"
                  className="tp-button tp-button--info add-item-editor__snap">
                  ↓ À la suite
                  <span className="add-item-editor__snap-detail">
                    — {new Date(lastItem.endTime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    {lastItem.program?.title ? ` · ${lastItem.program.title}` : lastItem.interludeClip?.name ? ` · ${lastItem.interludeClip.name}` : ''}
                  </span>
                </button>
              )}
            </div>

            {/* Durée — saisie libre sauf en mode AD_BLOCK où elle est calculée */}
            {itemType === 'AD_BLOCK' ? (
              <Field label={`Durée totale${duration ? ' · ' + fmtDuration(duration) : ''}`}>
                <div className="tp-input add-item-editor__readonly" aria-disabled="true">
                  {duration > 0
                    ? `${duration}s - spots + ${adBlockBlackSec}s de noir`
                    : '- ajoutez des spots pour calculer la duree'}
                </div>
              </Field>
            ) : itemType === 'INFOSERVICE' ? (
              <Field label="Plage infoservice">
                <div className="tp-input add-item-editor__readonly" aria-disabled="true">
                  {infoserviceRange ? fmtDuration(infoserviceRange.durationSec) : '— renseignez les deux heures'}
                </div>
              </Field>
            ) : itemType === 'LIVE' && createReplayProgram ? (
              <Field label={`Durée totale du bloc · ${fmtDuration(duration)}`}>
                <div className="tp-input add-item-editor__readonly" aria-disabled="true">
                  {livePartDurationsMin.length} partie{livePartDurationsMin.length > 1 ? 's' : ''}
                  {livePartDurationsMin.length > 1 ? ` · coupure ${fmtDuration(liveInterPartBreakSec)}` : ''}
                </div>
              </Field>
            ) : itemType === 'LIVE' ? (
              <Field label={`Durée estimée du direct (secondes) · ${fmtDuration(duration)}`}>
                <input
                  type="number"
                  min={1}
                  aria-label="Durée estimée du direct en secondes"
                  value={livePartDurationsSec[0] || ''}
                  onChange={event => setLivePartDurationsMin([String((Number(event.target.value) || 0) / 60)])}
                  placeholder="3600 (1h)"
                  className="tp-input"
                />
              </Field>
            ) : (
              <Field label={`Durée (secondes)${duration ? ' · ' + fmtDuration(duration) : ''}`}>
                <input type="number" min={1} value={duration || ''} onChange={e => setDuration(parseInt(e.target.value) || 0)} placeholder="3600" className="tp-input" />
              </Field>
            )}

            {/* ══ Section récurrence — désactivée pour les blocs pub ══════════ */}
            {(itemType === 'PROGRAM' || itemType === 'JINGLE') && (
            <div className={`add-item-recurrence${recEnabled ? ' is-enabled' : ''}`}>

              {/* Toggle récurrence */}
              <button
                onClick={() => setRecEnabled(v => !v)}
                aria-expanded={recEnabled}
                aria-pressed={recEnabled}
                className="add-item-recurrence__toggle"
                type="button"
              >
                <div className="add-item-recurrence__toggle-label">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M2 8a6 6 0 0 1 10.5-4M14 8a6 6 0 0 1-10.5 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                    <polyline points="12,2 14,4 12,6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    <polyline points="4,10 2,12 4,14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                  <span>
                    Récurrence
                  </span>
                </div>
                <div className="add-item-recurrence__switch" aria-hidden="true">
                  <div className="add-item-recurrence__switch-thumb" />
                </div>
              </button>

              {recEnabled && (
                <div className="add-item-recurrence__body">

                  {/* Fréquence */}
                  <div className="add-item-recurrence__row">
                    <span className="add-item-recurrence__label">Répéter</span>
                    <div className="add-item-recurrence__segments">
                      {(['DAILY', 'WEEKLY', 'MONTHLY'] as const).map(f => (
                        <button key={f} onClick={() => setRecFreq(f)} className={recFreq === f ? 'is-active' : ''} type="button">
                          {f === 'DAILY' ? 'Quotidien' : f === 'WEEKLY' ? 'Hebdo' : 'Mensuel'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Intervalle */}
                  <div className="add-item-recurrence__row">
                    <span className="add-item-recurrence__label">Toutes les</span>
                    <input
                      type="number" min={1} max={52} value={recInterval}
                      onChange={e => setRecInterval(Math.max(1, parseInt(e.target.value) || 1))}
                      className="tp-input add-item-recurrence__interval"
                    />
                    <span className="add-item-recurrence__label">
                      {recFreq === 'DAILY' ? recInterval > 1 ? 'jours' : 'jour' : recFreq === 'WEEKLY' ? recInterval > 1 ? 'semaines' : 'semaine' : recInterval > 1 ? 'mois' : 'mois'}
                    </span>
                  </div>

                  {/* Jours de semaine (WEEKLY) */}
                  {recFreq === 'WEEKLY' && (
                    <div>
                      <div className="add-item-recurrence__section-title">Jours de diffusion</div>
                      <div className="add-item-recurrence__days">
                        {DAY_LABELS.map((label, dow) => (
                          <button
                            key={dow}
                            onClick={() => toggleDow(dow)}
                            aria-pressed={recDaysOfWeek.includes(dow)}
                            className={recDaysOfWeek.includes(dow) ? 'is-active' : ''}
                            type="button"
                          >{label}</button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Jours du mois (MONTHLY) */}
                  {recFreq === 'MONTHLY' && (
                    <div>
                      <div className="add-item-recurrence__section-title">Jours du mois</div>
                      <div className="add-item-recurrence__month-days">
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(dom => (
                          <button
                            key={dom}
                            onClick={() => toggleDom(dom)}
                            aria-pressed={recDaysOfMonth.includes(dom)}
                            className={recDaysOfMonth.includes(dom) ? 'is-active' : ''}
                            type="button"
                          >{dom}</button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Fin de la récurrence */}
                  <div>
                    <div className="add-item-recurrence__section-title">Fin de la récurrence</div>
                    <div className="add-item-recurrence__end-options">
                      {([
                        ['never', 'Sans limite (365 max)'],
                        ['count', `Après N occurrences`],
                        ['date',  'Le'],
                      ] as const).map(([mode, label]) => (
                        <label key={mode} className="add-item-recurrence__end-option">
                          <input
                            type="radio" name="recEndMode" value={mode}
                            checked={recEndMode === mode}
                            onChange={() => setRecEndMode(mode)}
                          />
                          {label}
                          {mode === 'count' && recEndMode === 'count' && (
                            <input type="number" min={1} max={365} value={recCount}
                              onChange={e => setRecCount(Math.max(1, parseInt(e.target.value) || 1))}
                              className="tp-input add-item-recurrence__count" />
                          )}
                          {mode === 'count' && recEndMode === 'count' && (
                            <span className="add-item-recurrence__label">occurrences</span>
                          )}
                          {mode === 'date' && recEndMode === 'date' && (
                            <input type="date" value={recEndDate}
                              onChange={e => setRecEndDate(e.target.value)}
                              className="tp-input add-item-recurrence__end-date" />
                          )}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Résumé */}
                  {recSummary && (
                    <div className="add-item-editor__notice is-info">
                      🔁 {recSummary}
                      {recEndMode === 'never'          && ' · sans limite'}
                      {recEndMode === 'count'          && ` · ${recCount} fois`}
                      {recEndMode === 'date' && recEndDate && ` · jusqu'au ${new Date(recEndDate + 'T00:00:00').toLocaleDateString('fr-FR')}`}
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {error && (
              <div className="add-item-editor__notice is-error" role="alert">
                {error}
              </div>
            )}
          </div>
        </div>

        <div className="add-item-editor__footer">
          <button
            onClick={handleSubmit}
            disabled={loading || !canSubmit || (itemType === 'JINGLE' && occupiedWithoutInsertion) || !!trimPreview?.tooShort}
            className="tp-button tp-button--info"
            data-loading={loading || undefined}
          >
            {loading
              ? 'Planification…'
              : recEnabled        ? 'Planifier la série'
              : itemType === 'AD_BLOCK' ? 'Insérer le bloc pub'
              : itemType === 'INFOSERVICE' ? 'Insérer l’infoservice'
              : activePlan ? 'Insérer dans la grille'
              : 'Planifier'}
          </button>
        </div>
    </ModalShell>
  )
})

interface ModeButtonProps {
  active: boolean
  mode: (typeof MODE_DEFS)[number]
  onSelect: (mode: ItemMode) => void
}

const ModeButton = memo(function ModeButton({ active, mode, onSelect }: ModeButtonProps) {
  const handleSelect = useCallback(() => onSelect(mode.id), [mode.id, onSelect])

  return (
    <button
      aria-pressed={active}
      className={`add-item-editor__mode${active ? ' is-active' : ''}`}
      data-mode={mode.id.toLowerCase().replace('_', '-')}
      onClick={handleSelect}
      title={mode.title}
      type="button"
    >
      {mode.label}
    </button>
  )
})

const ScopeButton = memo(function ScopeButton({ def, active, onSelect }: {
  def:      (typeof SCOPE_DEFS)[number]
  active:   boolean
  onSelect: (scope: ProgramScope) => void
}) {
  const handleSelect = useCallback(() => onSelect(def.id), [def.id, onSelect])

  return (
    <button
      type="button"
      aria-pressed={active}
      className={active ? 'is-active' : ''}
      onClick={handleSelect}
    >
      {def.label}
    </button>
  )
})

const VersionChoice = memo(function VersionChoice({ asset, active, label, onSelect }: {
  asset: MediaAsset
  active: boolean
  label: string
  onSelect: (asset: MediaAsset) => void
}) {
  const handleClick = useCallback(() => onSelect(asset), [asset, onSelect])
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      className={active ? 'is-active' : ''}
      onClick={handleClick}
      title={asset.fileName}
    >
      {label}
    </button>
  )
})

const Field = memo(function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="add-item-field">
      <div className="agenda-editor__eyebrow">{label}</div>
      {children}
    </div>
  )
})

/**
 * Toggle "Inclure les directs ad-hoc" — élargit le sélecteur de programmes
 * pour faire apparaître les programmes créés inline depuis la grille.
 */
const AdHocToggle = memo(function AdHocToggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked)
  }, [onChange])

  return (
    <label className={`add-item-editor__toggle-card${checked ? ' is-selected' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={handleChange}
      />
      <div className="add-item-editor__toggle-copy">
        <div className="add-item-editor__toggle-title">
          Inclure les directs ad-hoc
        </div>
        <div className="add-item-editor__toggle-description">
          Affiche aussi les directs créés inline depuis la grille (utile pour planifier une rediffusion).
        </div>
      </div>
    </label>
  )
})

// ─── Sous-composants pour le mode AD_BLOCK (sélection multi-spots) ──────────

const AdBlockSpotRow = memo(function AdBlockSpotRow({
  entry, index, isLast, onMoveUp, onMoveDown, onRemove,
}: {
  entry:      AdBlockSpotEntry
  index:      number
  isLast:     boolean
  onMoveUp:   () => void
  onMoveDown: () => void
  onRemove:   () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.rowKey })

  return (
    <div
      ref={setNodeRef}
      className={`ad-spot-row${isDragging ? ' is-dragging' : ''}`}
      style={{ transform: CSS.Transform.toString(transform), transition } as CSSProperties}
    >
      <div className="ad-spot-row__index">{index + 1}</div>
      <button
        type="button"
        title="Deplacer"
        {...attributes}
        {...listeners}
        className="ad-spot-row__drag"
      >
        ::
      </button>
      <div className="ad-spot-row__main">
        <div className="ad-spot-row__title">
          {formatAdSpotLabel(entry)}
        </div>
        {entry.advertiserName && (
          <div className="ad-spot-row__meta">
            {entry.advertiserName}
          </div>
        )}
      </div>
      <div className="ad-spot-row__duration">
        {entry.durationSec}s
      </div>
      <div className="ad-spot-row__actions">
        <AdBlockArrowButton label="▲" onClick={onMoveUp} disabled={index === 0} title="Monter" />
        <AdBlockArrowButton label="▼" onClick={onMoveDown} disabled={isLast} title="Descendre" />
        <button type="button" onClick={onRemove} title="Retirer" className="ad-spot-row__button ad-spot-row__button--remove">×</button>
      </div>
    </div>
  )
})

const AdBlockArrowButton = memo(function AdBlockArrowButton({ label, onClick, disabled, title }: {
  label: string
  onClick: () => void
  disabled: boolean
  title: string
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} className="ad-spot-row__button">
      {label}
    </button>
  )
})

const AdBlockAddPanel = memo(function AdBlockAddPanel({
  catalog, search, onSearchChange, onPick, onCancel, loading,
}: {
  catalog:        AdBlockCatalogSpot[]
  search:         string
  onSearchChange: (v: string) => void
  onPick:         (s: AdBlockCatalogSpot) => void
  onCancel:       () => void
  loading:        boolean
}) {
  return (
    <div className="ad-spot-picker ad-spot-picker--embedded">
      <div className="ad-spot-picker__toolbar">
        <input
          autoFocus
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          placeholder="Filtrer par titre, campagne, annonceur…"
          className="tp-input ad-spot-picker__search"
        />
        <button
          type="button"
          onClick={onCancel}
          className="tp-button"
        >
          Fermer
        </button>
      </div>
      <div className="ad-spot-picker__list">
        {loading ? (
          <div className="agenda-editor__empty agenda-editor__empty--compact">
            <Spinner /> Chargement du catalogue…
          </div>
        ) : catalog.length === 0 ? (
          <div className="agenda-editor__empty agenda-editor__empty--compact">
            Aucun spot disponible. Créez-en dans Régie pub → Campagnes.
          </div>
        ) : catalog.map(s => (
          <button
            key={s.id}
            type="button"
            onClick={() => onPick(s)}
            className="ad-spot-picker__option"
          >
            <div className="ad-spot-picker__main">
              <div className="ad-spot-picker__title">
                {formatAdSpotLabel(s)} <MissingFileBadge presence={s.presence} />
              </div>
              {s.advertiser?.name && (
                <div className="ad-spot-picker__meta">
                  {s.advertiser.name}
                </div>
              )}
            </div>
            <div className="ad-spot-picker__duration">
              {s.durationSec}s
            </div>
          </button>
        ))}
      </div>
    </div>
  )
})
